/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

import { isQueryParamLocaleMode } from "../utils/locale-path.utils";

export interface LocalizedUrlResult {
	/** Path-based localized URL (e.g., /en-US/about) */
	localeSlug: string;
	/** Full URL with locale handling (uses lang param if NEXT_PUBLIC_LOCALE_MODE === "never") */
	fullUrl: string;
	/** Full URL without lang query param (for og:url, which should not include locale params) */
	plainUrl: string;
}

/**
 * Processes a slug and locale to generate localized URLs.
 * Handles both path-based locales (/en-US/page) and query param-based locales (?lang=en-US).
 *
 * @param locale - The locale code (e.g., "en-US")
 * @param slug - The page slug (e.g., "/about" or "/home")
 * @returns Object containing both the locale slug and full URL
 */
export const buildLocalizedUrl = (locale: string, slug: string, defaultLocale?: string): LocalizedUrlResult => {
	// Normalize the slug - handle "/home" special case
	const normalizedSlug = slug === "/home" ? "" : slug;

	// Build path-based locale URL
	const localeSlug = `/${locale}${normalizedSlug}`;

	// Use optional access here since metadata generation should gracefully degrade
	// to relative URLs when EPIC_SERVER_URL is not set (e.g., during build time).
	const baseUrl = `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}`;

	// Default to locale slug if lang param is disabled or no base URL
	let fullUrl: string = localeSlug;
	let plainUrl: string = localeSlug;

	if (isQueryParamLocaleMode() && baseUrl) {
		// Use slug for lang param (default to "/" for home)
		const slugForLangParam = normalizedSlug || "/";
		plainUrl = `${baseUrl}${slugForLangParam}`;

		// Default locale uses the clean URL as canonical (no ?lang= param)
		// Non-default locales get ?lang= to identify the language variant
		if (locale === defaultLocale) {
			fullUrl = plainUrl;
		} else {
			try {
				const url = new URL(plainUrl);
				url.searchParams.set("lang", locale);
				fullUrl = url.toString();
			} catch {
				fullUrl = `${plainUrl}?lang=${locale}`;
			}
		}
	}

	return {
		localeSlug,
		fullUrl,
		plainUrl,
	};
};
